import * as React from 'react';

import { FormControlWithStatus } from 'components/Form/FormControlVerificationStatus';
import { IDriverChecklistFields } from 'common/document/ui/driver-document';
import { InputWithStatus } from 'components/Form/FormControlVerificationStatus/Input';
import styles from './styles.module.scss';
import FormControl from '@mui/material/FormControl';
import { FormControlLabel, Radio, RadioGroup, TextField } from '@mui/material';
import { useMemo } from 'react';

type InputProps = {
  getFieldErrorProp: (field: keyof IDriverChecklistFields) => {
    error: string | undefined;
  };
  getFieldProps: (field: keyof IDriverChecklistFields) => {
    name: keyof IDriverChecklistFields;
    value: any;
    onChange: (inputValue: any) => void;
  };
  disabled: boolean;
  candidateFullName: string;
  employeeId?: string;
};

export const DriverChecklistCardForm = ({
  getFieldErrorProp,
  getFieldProps,
  disabled = true,
  candidateFullName,
  employeeId,
}: InputProps) => {
  const answers = useMemo(() => {
    // Check notifications = useMemo before adding a new question in src/features/Dashboard/DocumentView/DriverChecklistCardView/index.tsx
    return [
      {
        name: 'drugOrAlcohol',
        question:
          'In the past 6 months have you received or been recommended for drug or alcohol counseling?',
        smallAnswer: getFieldProps('drugOrAlcohol').value ? 'yes' : 'no',
        fullAnswer: getFieldProps('drugOrAlcoholExplanation').value,
        isCurrent: undefined,
      },
      {
        name: 'amphetamines',
        question:
          'In the past 6 months have you used amphetamines/stimulants of any kind?',
        smallAnswer: getFieldProps('amphetamines').value ? 'yes' : 'no',
        fullAnswer: getFieldProps('amphetaminesExplanation').value,
        isCurrent: undefined,
      },
      {
        name: 'opiates',
        question:
          'In the past 6 months have you used opiates/opioids (i.e. hydro, oxy, codeine, morphine, heroin)?',
        smallAnswer: getFieldProps('opiates').value ? 'yes' : 'no',
        fullAnswer: getFieldProps('opiatesExplanation').value,
        isCurrent: undefined,
      },
      {
        name: 'marijuana',
        question:
          'In the past 6 months have you used marijuana, hashish, or any THC?',
        smallAnswer: getFieldProps('marijuana').value ? 'yes' : 'no',
        fullAnswer: getFieldProps('marijuanaExplanation').value,
        isCurrent: undefined,
      },
      {
        name: 'cocaine',
        question: 'In the past 6 months have you used cocaine?',
        smallAnswer: getFieldProps('cocaine').value ? 'yes' : 'no',
        fullAnswer: getFieldProps('cocaineExplanation').value,
        isCurrent: undefined,
      },
      {
        name: 'pcp',
        question:
          'In the past 6 months have you used PCP or another hallucinogen?',
        smallAnswer: getFieldProps('pcp').value ? 'yes' : 'no',
        fullAnswer: getFieldProps('pcpExplanation').value,
        isCurrent: undefined,
      },
      {
        name: 'otherDrugs',
        question:
          'In the past 6 months have you used or taken any other illegal drug?',
        smallAnswer: getFieldProps('otherDrugs').value ? 'yes' : 'no',
        fullAnswer: getFieldProps('otherDrugsExplanation').value,
        isCurrent: undefined,
      },
      {
        name: 'hearing',
        question:
          'In the past 6 months have you been issued a waiver or exemption by US DOT for hearing or seizures?',
        smallAnswer: getFieldProps('hearing').value ? 'yes' : 'no',
        fullAnswer: '',
        isCurrent:
          getFieldProps('hearingIsCurrent').value === true
            ? 'yes'
            : getFieldProps('hearingIsCurrent').value === false
            ? 'no'
            : undefined,
      },
      {
        name: 'skillPerformanceEvaluation',
        question:
          'In the past 6 months have you obtained a Skill Performance Evaluation (SPE) certification from the US DOT?',
        smallAnswer: getFieldProps('skillPerformanceEvaluation').value
          ? 'yes'
          : 'no',
        fullAnswer: '',
        isCurrent:
          getFieldProps('skillPerformanceEvaluationIsCurrent').value === true
            ? 'yes'
            : getFieldProps('skillPerformanceEvaluationIsCurrent').value ===
              false
            ? 'no'
            : undefined,
      },
      {
        name: 'alternativeVisionStandard',
        question:
          'In the past 6 months have you been sent for an alternative vision standard via the MCSA-5871?',
        smallAnswer: getFieldProps('alternativeVisionStandard').value
          ? 'yes'
          : 'no',
        fullAnswer: '',
        isCurrent:
          getFieldProps('alternativeVisionStandardIsCurrent').value === true
            ? 'yes'
            : getFieldProps('alternativeVisionStandardIsCurrent').value ===
              false
            ? 'no'
            : undefined,
        isCurrentQuestion: 'Have you had the required road test?',
      },
    ];
  }, [getFieldProps]);

  return (
    <form>
      <div className={styles.row}>
        <FormControlWithStatus
          id="DriverName"
          label="Driver Name"
          disabled={disabled}
        >
          <InputWithStatus
            value={candidateFullName}
            id="DriverName"
            label="Driver Name"
            error={!!getFieldErrorProp('driverName').error || false}
            verified={true}
            hideVerification={false}
          />
        </FormControlWithStatus>
        <FormControlWithStatus
          id="AlphaCode"
          label="Alpha Code"
          disabled={disabled}
        >
          <InputWithStatus
            value={employeeId}
            id="AlphaCode"
            label="Alpha Code"
            error={!!getFieldErrorProp('alphaCode').error || false}
            verified={true}
            hideVerification={false}
          />
        </FormControlWithStatus>
      </div>
      {answers.map((item, index) => (
        <div className={styles.rowAnswer} key={item.question}>
          <div>
            {index + 1}. {item.question}
          </div>
          <div className={styles.formElements}>
            <FormControl disabled={disabled}>
              <RadioGroup row name={item.name} value={item.smallAnswer}>
                <FormControlLabel
                  value="yes"
                  control={<Radio sx={{ '& svg': { fill: '#FFDB00' } }} />}
                  label="Yes"
                />
                <FormControlLabel
                  value="no"
                  control={<Radio sx={{ '& svg': { fill: '#FFDB00' } }} />}
                  label="No"
                />
              </RadioGroup>
            </FormControl>
            {item.smallAnswer === 'yes' && (
              <>
                {item.fullAnswer && (
                  <FormControl>
                    <TextField
                      id="outlined-textarea"
                      label="Amphetamine/Stimulant Use Reason"
                      placeholder="Amphetamine/Stimulant Use Reason"
                      multiline
                      disabled={disabled}
                      value={item.fullAnswer}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23) !important',
                          backgroundColor: '#F7F8F9',
                        },
                        '& label.Mui-disabled': {
                          backgroundColor: '#FFF',
                        },
                        '& textarea.Mui-disabled': {
                          zIndex: 9,
                        },
                      }}
                    />
                  </FormControl>
                )}
                {item.isCurrent && (
                  <FormControl disabled={disabled}>
                    <div>
                      {index + 1}a. {item.isCurrentQuestion ?? 'Is Current?'}
                    </div>
                    <RadioGroup
                      row
                      name={item.name + 'current'}
                      value={item.isCurrent}
                    >
                      <FormControlLabel
                        value="yes"
                        control={
                          <Radio sx={{ '& svg': { fill: '#FFDB00' } }} />
                        }
                        label="Yes"
                      />
                      <FormControlLabel
                        value="no"
                        control={
                          <Radio sx={{ '& svg': { fill: '#FFDB00' } }} />
                        }
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                )}
              </>
            )}
          </div>
        </div>
      ))}
    </form>
  );
};
